import type {
  WorkflowNodeEntity,
} from '@flowgram.ai/free-layout-editor';
import type {
  FlowNodeData,
  ModelConfig,
} from '$/domains/workFlowV3/type';
import { useLanguageStore } from '$/store/languageStore';
import type { LanguageKeyMap } from '$/store/languageStore/smartcatTranslation';
import zhTranslation from '$/store/languageStore/smartcatTranslation/zh.json';
import { getVideoCoverUrl } from '$/utils/upload';
import quickStartConfigJson from '../config/quickStart.config.json';
import {
  NODE_NAME_CONFIG,
  TVFlowNodeNextTypeEnum,
} from '../config/node.config';
import {
  buildMcpInfoFromGenerationConfig,
  getCurrentToolParameters,
  getGenerationConfigByModelAndTool,
} from './modelConfig';
import type {
  CurrentDataPatch,
  LeftLinkedNodeConfig,
  LeftLinkedNodePoint,
} from './applyLeftLinkedNodes';
import { markQuickStartDismissed } from './quickStartVisibility';

interface QuickStartMaterialConfig {
  url?: string;
  width?: number;
  height?: number;
  duration?: number;
  cover_url?: string;
  watermark_url?: string;
  watermark_cover_url?: string;
}

interface QuickStartLeftInputConfig {
  type: string;
  name?: string;
  text?: string;
  material?: QuickStartMaterialConfig;
  offset?: LeftLinkedNodePoint;
}

interface QuickStartConfigItem {
  title: string;
  targetNodeType: string;
  model?: string;
  apiName?: string;
  nodeModelId?: string;
  prompt?: string;
  leftInputs?: QuickStartLeftInputConfig[];
}

type LinkedNodeData = Record<string, any> & {
  inputNodeId?: FlowNodeData['inputNodeId'];
};

const QUICK_START_CONFIG = quickStartConfigJson as Record<string, QuickStartConfigItem>;

const TRANSLATION_KEY_SET = new Set(Object.keys(zhTranslation));

/**
 * 判断快速开始配置值是否为 smartcat 翻译 key。
 */
function isQuickStartTranslationKey(value: string): value is keyof LanguageKeyMap {
  return TRANSLATION_KEY_SET.has(value);
}

/**
 * 将配置中的翻译 key 解析为当前语言文案；非 key 时原样返回。
 */
function resolveQuickStartI18nText(value: string | undefined): string | undefined {
  if (!value) {
    return value;
  }
  if (!isQuickStartTranslationKey(value)) {
    return value;
  }
  const { languageStore } = useLanguageStore.getState();
  return languageStore.smartcatTranslationText((v) => v[value]);
}

/**
 * 解析快速开始左侧输入配置中的可翻译字段。
 */
function resolveQuickStartLeftInput(input: QuickStartLeftInputConfig): QuickStartLeftInputConfig {
  return {
    ...input,
    name: input.name ? resolveQuickStartI18nText(input.name) : input.name,
    text: input.text ? resolveQuickStartI18nText(input.text) : input.text,
  };
}

/**
 * 解析快速开始项配置中的可翻译字段。
 */
function resolveQuickStartConfigItem(config: QuickStartConfigItem): QuickStartConfigItem {
  return {
    ...config,
    title: resolveQuickStartI18nText(config.title) ?? config.title,
    prompt: resolveQuickStartI18nText(config.prompt),
    leftInputs: config.leftInputs?.map(resolveQuickStartLeftInput),
  };
}

const PROMPT_REFERENCE_TOKEN_REGEXP = /\[@(image|video|audio)\]/g;

const PROMPT_REFERENCE_NODE_TYPE_MAP: Record<string, TVFlowNodeNextTypeEnum> = {
  image: TVFlowNodeNextTypeEnum.IMAGE_NODE,
  video: TVFlowNodeNextTypeEnum.VIDEO_NODE,
  audio: TVFlowNodeNextTypeEnum.AUDIO_NODE,
};

const PROMPT_REFERENCE_TAG_MAP: Record<string, string> = {
  image: 'input_image',
  video: 'input_video',
  audio: 'input_file',
};

export type QuickStartType = keyof typeof quickStartConfigJson;

export interface ResolveQuickStartApplyConfigOptions {
  quickStartType: QuickStartType | string;
  modelConfig?: ModelConfig;
}

export interface ResolvedQuickStartApplyConfig<TCurrentData extends LinkedNodeData> {
  leftNodes: LeftLinkedNodeConfig[];
  currentDataPatch: CurrentDataPatch<TCurrentData>;
}

/**
 * 判断传入 action key 是否存在快速开始配置，避免节点侧硬编码配置细节。
 */
export function hasQuickStartConfig(quickStartType: string): quickStartType is QuickStartType {
  return Object.prototype.hasOwnProperty.call(QUICK_START_CONFIG, quickStartType);
}

/**
 * 将配置里的节点类型字符串收敛为画布节点枚举。
 */
function resolveNodeType(nodeType: string | undefined): TVFlowNodeNextTypeEnum | undefined {
  if (!nodeType) return undefined;
  return Object.values(TVFlowNodeNextTypeEnum).includes(nodeType as TVFlowNodeNextTypeEnum)
    ? nodeType as TVFlowNodeNextTypeEnum
    : undefined;
}

/**
 * 从配置或 store 中取得模型配置，减少快速开始调用侧参数。
 */

/**
 * 获取某节点类型默认的 mcpInfo，主要用于快速开始创建出来的左侧输入节点。
 */
function buildDefaultMcpInfo(
  nodeType: TVFlowNodeNextTypeEnum,
) {
  const generationConfig = getGenerationConfigByModelAndTool(nodeType);
  return buildMcpInfoFromGenerationConfig(generationConfig, generationConfig?.parameters ?? {});
}

/**
 * 将配置中的 [@image]/[@video]/[@audio] 占位替换为当前实际创建出的左侧节点引用。
 */
function serializeQuickStartPromptReferences(
  prompt: string | undefined,
  leftNodes: WorkflowNodeEntity[],
) {
  if (typeof prompt !== 'string') return prompt;
  const counters: Record<string, number> = {};

  return prompt.replace(PROMPT_REFERENCE_TOKEN_REGEXP, (token, mediaType: string) => {
    const nodeType = PROMPT_REFERENCE_NODE_TYPE_MAP[mediaType];
    const tag = PROMPT_REFERENCE_TAG_MAP[mediaType];
    if (!nodeType || !tag) return token;

    const sameTypeNodes = leftNodes.filter((node) => node.flowNodeType === nodeType);
    const index = counters[mediaType] ?? 0;
    counters[mediaType] = index + 1;
    const targetNode = sameTypeNodes[index] ?? sameTypeNodes[0];

    return targetNode ? `[@${tag}:${targetNode.id}]` : token;
  });
}

/**
 * 根据快速开始配置生成当前目标节点的 mcpInfo，并把配置 prompt 写入真实 prompt 参数。
 */
function buildCurrentNodeMcpInfo(
  nodeType: TVFlowNodeNextTypeEnum,
  config: QuickStartConfigItem,
  prompt: string | undefined,
) {
  const generationConfig = getGenerationConfigByModelAndTool(
    nodeType,
    config.model,
    config.apiName,
    config.nodeModelId,
  );
  const parameters = {
    ...(generationConfig?.parameters ?? {}),
  };
  const promptKey = Object.entries(getCurrentToolParameters(nodeType, generationConfig))
    .find(([, parameter]) => parameter?.type === 'prompt')?.[0];

  if (promptKey && typeof prompt === 'string') {
    parameters[promptKey] = prompt;
  }

  return buildMcpInfoFromGenerationConfig(generationConfig, parameters);
}

/**
 * 统一生成文本类节点 media_list，文本节点和脚本节点都消费 value 字段。
 */
function createTextMediaList(value = '') {
  return [{ value }];
}

/**
 * 按当前目标节点类型生成空结果列表，避免媒体节点被写入文本节点的 value 结构。
 */
function createCurrentNodeMediaList(targetNodeType: TVFlowNodeNextTypeEnum) {
  if (
    targetNodeType === TVFlowNodeNextTypeEnum.TEXT_NODE
    || targetNodeType === TVFlowNodeNextTypeEnum.SCRIPT_NODE
  ) {
    return createTextMediaList('');
  }

  return [];
}

/**
 * 生成快速开始的文本左侧输入节点数据。
 */
function createTextNodeData(
  input: QuickStartLeftInputConfig,
) {
  const name = input.name || NODE_NAME_CONFIG[TVFlowNodeNextTypeEnum.TEXT_NODE];
  return {
    name,
    title: name,
    isCustomer: true,
    isRecommend: true,
    media_list: createTextMediaList(input.text || ''),
    mcpInfo: buildDefaultMcpInfo(TVFlowNodeNextTypeEnum.TEXT_NODE),
  };
}

/**
 * 生成快速开始的图片左侧输入节点数据；空 url 保持图片节点初始态。
 */
function createImageNodeData(
  input: QuickStartLeftInputConfig,
) {
  const name = input.name || NODE_NAME_CONFIG[TVFlowNodeNextTypeEnum.IMAGE_NODE];
  const url = input.material?.url?.trim() || '';
  return {
    name,
    title: name,
    isCustomer: true,
    isRecommend: true,
    status: url ? 'loaded' : 'empty',
    media_list: url ? [{
      url,
      originUrl: url,
      watermark_url: input.material?.watermark_url || url,
      width: input.material?.width,
      height: input.material?.height,
      name,
      source: 'generation',
    }] : [],
    mcpInfo: buildDefaultMcpInfo(TVFlowNodeNextTypeEnum.IMAGE_NODE),
  };
}

/**
 * 生成快速开始的视频左侧输入节点数据；空 url 保持视频节点上传态。
 */
function createVideoNodeData(
  input: QuickStartLeftInputConfig,
) {
  const name = input.name || NODE_NAME_CONFIG[TVFlowNodeNextTypeEnum.VIDEO_NODE];
  const url = input.material?.url?.trim() || '';
  const coverUrl = input.material?.cover_url?.trim() || getVideoCoverUrl(url);
  return {
    name,
    title: name,
    isCustomer: true,
    isRecommend: true,
    media_list: url ? [{
      url,
      width: input.material?.width,
      height: input.material?.height,
      duration: input.material?.duration,
      cover_url: coverUrl,
      watermark_url: input.material?.watermark_url || url,
      watermark_cover_url: input.material?.watermark_cover_url || coverUrl,
    }] : [],
    mcpInfo: buildDefaultMcpInfo(TVFlowNodeNextTypeEnum.VIDEO_NODE),
  };
}

/**
 * 生成快速开始的音频左侧输入节点数据；空 url 保持音频节点上传态。
 */
function createAudioNodeData(
  input: QuickStartLeftInputConfig,
) {
  const name = input.name || NODE_NAME_CONFIG[TVFlowNodeNextTypeEnum.AUDIO_NODE];
  const url = input.material?.url?.trim() || '';
  return {
    name,
    title: name,
    isCustomer: true,
    isRecommend: true,
    media_list: url ? [{
      url,
      duration: input.material?.duration,
    }] : [],
    mcpInfo: buildDefaultMcpInfo(TVFlowNodeNextTypeEnum.AUDIO_NODE),
  };
}

/**
 * 根据单条左侧输入配置，创建 applyLeftLinkedNodes 可消费的节点配置。
 */
function createLeftNodeConfig(
  input: QuickStartLeftInputConfig,
): LeftLinkedNodeConfig | null {
  const nodeType = resolveNodeType(input.type);
  if (!nodeType) return null;

  const dataFactoryMap: Partial<Record<TVFlowNodeNextTypeEnum, () => Record<string, unknown>>> = {
    [TVFlowNodeNextTypeEnum.TEXT_NODE]: () => createTextNodeData(input),
    [TVFlowNodeNextTypeEnum.IMAGE_NODE]: () => createImageNodeData(input),
    [TVFlowNodeNextTypeEnum.VIDEO_NODE]: () => createVideoNodeData(input),
    [TVFlowNodeNextTypeEnum.AUDIO_NODE]: () => createAudioNodeData(input),
  };
  const createData = dataFactoryMap[nodeType];
  if (!createData) return null;

  return {
    type: nodeType,
    data: createData(),
    offset: input.offset,
  };
}

/**
 * 为当前节点生成配置驱动的 data patch，保留文本/脚本各自的业务状态字段。
 */
function createCurrentDataPatch<TCurrentData extends LinkedNodeData>(
  quickStartType: string,
  targetNodeType: TVFlowNodeNextTypeEnum,
  config: QuickStartConfigItem,
): CurrentDataPatch<TCurrentData> {
  return ({ currentData, leftNodes }) => {
    const prompt = serializeQuickStartPromptReferences(config.prompt, leftNodes);
    const currentNodeMcpInfo = buildCurrentNodeMcpInfo(targetNodeType, config, prompt)
      ?? currentData.mcpInfo;
    const nextData = markQuickStartDismissed({
      name: currentData.name || NODE_NAME_CONFIG[targetNodeType],
      isCustomer: false,
      isRecommend: false,
      media_list: createCurrentNodeMediaList(targetNodeType) as TCurrentData['media_list'],
      ...(currentNodeMcpInfo ? { mcpInfo: currentNodeMcpInfo } : {}),
    }) as unknown as Partial<TCurrentData>;

    if (quickStartType === 'prompt') {
      return {
        ...nextData,
        action: 'prompt_generate',
        promptState: 'generating',
      } as Partial<TCurrentData>;
    }

    if (quickStartType === 'imageReversePrompt') {
      return {
        ...nextData,
        action: 'image_reverse_prompt',
        reversePromptState: 'generating',
        taskInfo: {
          ...((currentData.taskInfo as Record<string, unknown> | undefined) || {}),
          action: 'image_reverse_prompt',
          loading: true,
          status: 1,
        },
      } as Partial<TCurrentData>;
    }

    return nextData;
  };
}

/**
 * 将快速开始类型解析为 applyLeftLinkedNodes 的左侧节点配置与当前节点补丁。
 */
export function resolveQuickStartApplyConfig<TCurrentData extends LinkedNodeData>(
  options: ResolveQuickStartApplyConfigOptions,
): ResolvedQuickStartApplyConfig<TCurrentData> | null {
  if (!hasQuickStartConfig(options.quickStartType)) {
    return null;
  }

  const config = resolveQuickStartConfigItem(QUICK_START_CONFIG[options.quickStartType]);
  const targetNodeType = resolveNodeType(config.targetNodeType);
  if (!targetNodeType) {
    return null;
  }

  const leftNodes = (config.leftInputs ?? [])
    .map((input) => createLeftNodeConfig(input))
    .filter((item): item is LeftLinkedNodeConfig => Boolean(item));

  return {
    leftNodes,
    currentDataPatch: createCurrentDataPatch(
      options.quickStartType,
      targetNodeType,
      config,
    ),
  };
}
